import { hasuraClient } from '../utils/graphqlClient';
import {
  LOGIN_MUTATION,
  LOGOUT_MUTATION,
  REFRESH_TOKEN_MUTATION,
} from '../graphql/mutations/auth';

/**
 * @typedef {Object} LoginCredentials
 * @property {string} emailAddress - User's email address
 * @property {string} password - User's password
 */

/**
 * @typedef {Object} AuthTokens
 * @property {string} accessToken - JWT access token
 * @property {string} refreshToken - JWT refresh token
 */

/**
 * @typedef {Object} UserProfile
 * @property {string} id - User ID
 * @property {string} email - User email
 * @property {string} firstName - User first name
 * @property {string} lastName - User last name
 * @property {string} name - Full name
 * @property {string} role - User role
 * @property {string} companyId - Company ID
 * @property {string} [team_id] - Team ID
 * @property {string} [title_id] - Title ID
 * @property {string} [title_code] - Title code
 * @property {string} [title_name] - Title name
 * @property {Object} [team] - Team information
 * @property {string} team.id - Team ID
 * @property {string} team.name - Team name
 * @property {string} team.group_id - Group ID
 * @property {Object} [team.group] - Group information
 * @property {string} team.group.id - Group ID
 * @property {string} team.group.name - Group name
 */

/**
 * @typedef {Object} AuthResult
 * @property {boolean} success - Operation success status
 * @property {UserProfile} [user] - User profile (on success)
 * @property {string} [token] - Access token (on success)
 * @property {string} [refreshToken] - Refresh token (on success)
 * @property {string} [error] - Error message (on failure)
 * @property {string} [message] - Success message
 */

export class AuthenticationService {
  static STORAGE_KEYS = Object.freeze({
    ACCESS_TOKEN: 'auth_token',
    REFRESH_TOKEN: 'auth_refresh_token',
    USER_PROFILE: 'auth_user_profile',
  });

  constructor(graphqlClient = hasuraClient, storage = localStorage) {
    this._graphqlClient = graphqlClient;
    this._storage = storage;
  }

  /**
   * Authenticate user with credentials
   * @param {LoginCredentials} credentials - Login credentials
   * @returns {Promise<AuthResult>} Authentication result
   */
  async authenticate(credentials) {
    try {
      this._validateCredentials(credentials);

      const response = await this._graphqlClient.mutate(LOGIN_MUTATION, {
        input: credentials,
      });

      const loginData = response.login;

      if (!this._isValidLoginResponse(loginData)) {
        return this._createErrorResult('Geçersiz sunucu yanıtı');
      }

      // Extract tokens first and save them immediately so they're available for subsequent queries
      const tokens = this._extractTokens(loginData);

      // Save access token immediately so it can be used in the next query
      this._storage.setItem(
        AuthenticationService.STORAGE_KEYS.ACCESS_TOKEN,
        tokens.token
      );
      if (tokens.refreshToken) {
        this._storage.setItem(
          AuthenticationService.STORAGE_KEYS.REFRESH_TOKEN,
          tokens.refreshToken
        );
      }

      // Fetch complete user profile with team and group information
      // Now the token is available in localStorage, so the query will use it
      let completeUserData = null;
      try {
        const { GET_USER_WITH_TEAM_QUERY } = await import(
          '../graphql/queries/auth'
        );
        const userResponse = await this._graphqlClient.query(
          GET_USER_WITH_TEAM_QUERY,
          {
            id: loginData.id,
          },
          {
            skipAuth: false, // Explicitly use JWT token
          }
        );

        if (userResponse?.users_by_pk) {
          completeUserData = userResponse.users_by_pk;
        }
      } catch (queryError) {
        console.warn(
          'Failed to fetch user profile from Hasura, using login response data:',
          queryError.message
        );
        // If query fails, create minimal profile from login response
        completeUserData = {
          id: loginData.id,
          email: loginData.emailAddress,
          first_name: loginData.firstName,
          last_name: loginData.lastName,
          role: loginData.role,
          company_id: loginData.companyId,
          team_id: loginData.teamId || null,
          title_id: null,
          user_birthday: null,
          company: null,
          team: null,
          title: null,
        };
      }

      // If query didn't return data, use login response data as fallback
      if (!completeUserData) {
        completeUserData = {
          id: loginData.id,
          email: loginData.emailAddress,
          first_name: loginData.firstName,
          last_name: loginData.lastName,
          role: loginData.role,
          company_id: loginData.companyId,
          team_id: loginData.teamId || null,
          title_id: null,
          user_birthday: null,
          company: null,
          team: null,
          title: null,
        };
      }

      const userProfile = this._mapToUserProfile(completeUserData);

      // Save complete user profile
      await this._persistAuthData(userProfile, tokens);

      return this._createSuccessResult({
        user: userProfile,
        ...tokens,
      });
    } catch (error) {
      return this._createErrorResult(this._translateError(error));
    }
  }

  /**
   * Invalidate user session
   * @returns {Promise<AuthResult>} Logout result
   */
  async invalidateSession() {
    try {
      const refreshToken = this.getRefreshToken();

      if (refreshToken) {
        try {
          await this._graphqlClient.mutate(LOGOUT_MUTATION, {
            input: { refreshToken },
          });
        } catch (serverError) {
          console.warn('Server logout failed:', serverError.message);
        }
      }

      this._clearAuthData();

      return this._createSuccessResult({
        message: 'Başarıyla çıkış yaptınız',
      });
    } catch (error) {
      this._clearAuthData();
      return this._createSuccessResult({
        message: 'Çıkış yapıldı',
      });
    }
  }

  /**
   * Refresh authentication tokens
   * @returns {Promise<AuthResult>} Refresh result
   */
  async refreshTokens() {
    try {
      const currentRefreshToken = this.getRefreshToken();

      if (!currentRefreshToken) {
        throw new Error('Refresh token bulunamadı');
      }

      const response = await this._graphqlClient.mutate(
        REFRESH_TOKEN_MUTATION,
        {
          input: { refreshToken: currentRefreshToken },
        },
        {
          skipAuth: true, // Refresh token sırasında access token expire olmuş olabilir
        }
      );

      const refreshData = response.refreshToken;

      if (!refreshData?.accessToken) {
        throw new Error('Geçersiz token yanıtı');
      }

      const newTokens = {
        token: refreshData.accessToken,
        refreshToken: refreshData.refreshToken || currentRefreshToken,
      };

      this._updateStoredTokens(newTokens);

      return this._createSuccessResult(newTokens);
    } catch (error) {
      this._clearAuthData();
      return this._createErrorResult(this._translateError(error));
    }
  }

  /**
   * Get current access token
   * @returns {string|null} Access token
   */
  getAccessToken() {
    return this._storage.getItem(
      AuthenticationService.STORAGE_KEYS.ACCESS_TOKEN
    );
  }

  /**
   * Get current refresh token
   * @returns {string|null} Refresh token
   */
  getRefreshToken() {
    return this._storage.getItem(
      AuthenticationService.STORAGE_KEYS.REFRESH_TOKEN
    );
  }

  /**
   * Get current user profile
   * @returns {UserProfile|null} User profile
   */
  getUserProfile() {
    try {
      const profileData = this._storage.getItem(
        AuthenticationService.STORAGE_KEYS.USER_PROFILE
      );
      return profileData ? JSON.parse(profileData) : null;
    } catch (error) {
      this._clearAuthData();
      return null;
    }
  }

  /**
   * Fetch complete user profile with team and group information
   * @param {number} userId - User ID
   * @returns {Promise<UserProfile|null>} Complete user profile
   */
  async fetchCompleteUserProfile(userId) {
    try {
      const { GET_USER_WITH_TEAM_QUERY } = await import(
        '../graphql/queries/auth'
      );
      const response = await this._graphqlClient.query(
        GET_USER_WITH_TEAM_QUERY,
        {
          id: userId,
        }
      );

      const userData = response.users_by_pk;
      if (userData) {
        return this._mapToUserProfile(userData);
      }
      return null;
    } catch (error) {
      console.error('Error fetching complete user profile:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    const token = this.getAccessToken();
    const user = this.getUserProfile();
    return !!(token && user);
  }

  /**
   * Sunucudan session validation. GET /auth/me — pasif kullanicida 401
   * doner. Kullanim yerleri:
   *  - App startup (authSlice.initializeAuth)
   *  - graphqlClient 401 handler (aynı user olarak Hasura'ya istek
   *    atarken pasif oldugumuzu ogrenirsek session kesilir)
   *
   * Returns:
   *  { ok: true, user: {...} }              — hala aktif
   *  { ok: false, status: 401, reason }     — pasif / token gecersiz
   *  { ok: false, status: 0, reason }       — network hatasi (session'i
   *                                            hemen kesme, retry mumkun)
   */
  async validateSession() {
    const token = this.getAccessToken();
    if (!token) return { ok: false, status: 401, reason: 'no-token' };
    const baseUrl = process.env.REACT_APP_BACKEND_API_URL;
    if (!baseUrl) return { ok: false, status: 0, reason: 'no-auth-url' };
    try {
      const res = await fetch(`${baseUrl}/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'x-api-key': process.env.REACT_APP_API_KEY || '',
        },
      });
      if (res.ok) {
        const user = await res.json();
        return { ok: true, user };
      }
      return { ok: false, status: res.status, reason: 'inactive-or-invalid' };
    } catch (err) {
      // Network / CORS / DNS → session'i hemen KESME, kullanici cevrimdisi
      // olabilir. isAuthenticated hala storage'a bakiyor.
      return { ok: false, status: 0, reason: err?.message || 'network-error' };
    }
  }

  /**
   * Check if refresh token is available
   * @returns {boolean} Refresh token availability
   */
  hasRefreshToken() {
    return !!this.getRefreshToken();
  }

  /**
   * Validate login credentials
   * @private
   * @param {LoginCredentials} credentials
   */
  _validateCredentials(credentials) {
    if (!credentials.emailAddress || !credentials.password) {
      throw new Error('Email ve şifre gereklidir');
    }

    if (!this._isValidEmail(credentials.emailAddress)) {
      throw new Error('Geçersiz email formatı');
    }
  }

  /**
   * Validate email format
   * @private
   * @param {string} email
   * @returns {boolean}
   */
  _isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Check if login response is valid
   * @private
   * @param {Object} loginData
   * @returns {boolean}
   */
  _isValidLoginResponse(loginData) {
    return !!(
      loginData?.accessToken &&
      loginData?.id &&
      loginData?.emailAddress
    );
  }

  /**
   * Map API response to user profile
   * @private
   * @param {Object} userData
   * @returns {UserProfile}
   */
  _mapToUserProfile(userData) {
    return {
      id: userData.id,
      email: userData.email || userData.emailAddress,
      firstName: userData.first_name || userData.firstName,
      lastName: userData.last_name || userData.lastName,
      name: `${userData.first_name || userData.firstName} ${userData.last_name || userData.lastName}`.trim(),
      role: userData.role,
      companyId: userData.company_id || userData.companyId,
      company: userData.company
        ? {
            id: userData.company.id,
            name: userData.company.name,
          }
        : null,
      team_id: userData.team_id,
      group_id: userData.team?.group_id || userData.group_id,
      title_id: userData.title_id,
      title_code: userData.title?.title_code || null,
      is_expense_manager: userData.is_expense_manager === true,
      title_name: userData.title?.title_name || null,
      workPhoneNumber: userData.work_phone_number || null,
      birthDate: userData.user_birthday
        ? new Date(userData.user_birthday)
        : null,
      isLoanRequestManager: userData.is_loan_request_manager === true,
      manages_collections: userData.manages_collections === true,
      canViewCommunicationReport:
        userData.can_view_communication_report === true,
      team: userData.team
        ? {
            id: userData.team.id,
            name: userData.team.name,
            group_id: userData.team.group_id,
            group: userData.team.group
              ? {
                  id: userData.team.group.id,
                  name: userData.team.group.name,
                }
              : null,
          }
        : null,
    };
  }

  /**
   * Extract tokens from login response
   * @private
   * @param {Object} loginData
   * @returns {AuthTokens}
   */
  _extractTokens(loginData) {
    return {
      token: loginData.accessToken,
      refreshToken: loginData.refreshToken,
    };
  }

  /**
   * Persist authentication data to storage
   * @private
   * @param {UserProfile} userProfile
   * @param {AuthTokens} tokens
   */
  async _persistAuthData(userProfile, tokens) {
    try {
      this._storage.setItem(
        AuthenticationService.STORAGE_KEYS.ACCESS_TOKEN,
        tokens.token
      );
      this._storage.setItem(
        AuthenticationService.STORAGE_KEYS.USER_PROFILE,
        JSON.stringify(userProfile)
      );

      if (tokens.refreshToken) {
        this._storage.setItem(
          AuthenticationService.STORAGE_KEYS.REFRESH_TOKEN,
          tokens.refreshToken
        );
      }
    } catch (error) {
      throw new Error('Oturum bilgileri kaydedilemedi');
    }
  }

  /**
   * Update stored tokens
   * @private
   * @param {AuthTokens} tokens
   */
  _updateStoredTokens(tokens) {
    this._storage.setItem(
      AuthenticationService.STORAGE_KEYS.ACCESS_TOKEN,
      tokens.token
    );

    if (tokens.refreshToken) {
      this._storage.setItem(
        AuthenticationService.STORAGE_KEYS.REFRESH_TOKEN,
        tokens.refreshToken
      );
    }
  }

  /**
   * Clear all authentication data
   * @private
   */
  _clearAuthData() {
    Object.values(AuthenticationService.STORAGE_KEYS).forEach(key => {
      this._storage.removeItem(key);
    });
  }

  /**
   * Translate error to user-friendly message
   * @private
   * @param {Error} error
   * @returns {string}
   */
  _translateError(error) {
    const message = error.message?.toLowerCase() || '';

    if (error.type === 'GRAPHQL_ERROR') {
      return error.message;
    }

    if (error.type === 'NETWORK_ERROR' || error.type === 'TIMEOUT_ERROR') {
      return 'Ağ bağlantı hatası. İnternet bağlantınızı kontrol edin.';
    }

    if (error.type === 'TOKEN_EXPIRED') {
      return 'Oturum süresi dolmuş. Lütfen tekrar giriş yapın.';
    }

    if (error.type === 'PERMISSION_ERROR') {
      return 'Bu işlem için yetkiniz bulunmuyor.';
    }

    const errorMap = {
      'invalid email address or password': 'E-posta veya şifre hatalı.',
      'invalid credentials': 'E-posta veya şifre hatalı.',
      'http exception when calling webhook':
        'Kimlik doğrulama servisi erişilemiyor.',
      'network error':
        'Ağ bağlantı hatası. İnternet bağlantınızı kontrol edin.',
      'server error': 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.',
      'authentication failed': 'Kimlik doğrulama başarısız.',
      'email ve şifre gereklidir': 'E-posta ve şifre gereklidir.',
      'geçersiz email formatı': 'Geçersiz e-posta formatı.',
      'oturum bilgileri kaydedilemedi': 'Oturum bilgileri kaydedilemedi.',
    };

    if (errorMap[message]) {
      return errorMap[message];
    }

    for (const [key, translation] of Object.entries(errorMap)) {
      if (message.includes(key.toLowerCase())) {
        return translation;
      }
    }

    if (error.message) {
      return error.message;
    }

    return 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.';
  }

  /**
   * Create success result
   * @private
   * @param {Object} data
   * @returns {AuthResult}
   */
  _createSuccessResult(data) {
    return {
      success: true,
      ...data,
    };
  }

  /**
   * Create error result
   * @private
   * @param {string} error
   * @returns {AuthResult}
   */
  _createErrorResult(error) {
    return {
      success: false,
      error,
    };
  }
}

const authService = new AuthenticationService();

export default authService;
